import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Cart, Session, CartStateService, SessionService } from '@hypenation/tickets';
import { Router } from '@angular/router';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { ShoppingCartComponent } from "../shopping-cart/shopping-cart.component";
import { AuthStateService, getEmailFromToken } from '@hypenation/auth';
import { Subscription } from 'rxjs';
import { environment } from '@env/environment';

@Component({
  selector: 'hypenation-shared-navbar',
  standalone: true,
  imports: [CommonModule, FormsModule, NzBadgeModule, NzIconModule, ShoppingCartComponent],
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.css',
})
export class NavbarComponent implements OnInit, OnDestroy {
  isMobileMenuOpen = false;
  isUserMenuOpen = false;
  userEmail: string | null = null;
  userInitials: string = '';

  private readonly _authStateService = inject(AuthStateService);
  private documentClickHandler = (event: MouseEvent) => this.onDocumentClick(event);
  private authStateSubscription?: Subscription;
  private cartUpdateSubscription?: Subscription;

  toggleMobileMenu() {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  toggleUserMenu() {
    this.isUserMenuOpen = !this.isUserMenuOpen;
  }

  closeUserMenu() {
    this.isUserMenuOpen = false;
  }

  get activeLink() {
    return this._router.url
  }




  cart: Cart | null = null;
  session: Session | null = null;
  searchText = '';
  numberOfItems = 0;

  constructor(
    private readonly _router: Router,
    private readonly _cartStateService: CartStateService,
    private readonly _sessionService: SessionService
  ) {}



  public navigateToCart(): void {
    // If the user has already kicked off checkout but not yet paid, route
    // them back to the in-flight checkout instead of /cart/{uid} (which
    // would now be empty server-side because the items were moved into
    // the order on /checkout).
    const pendingOrderNumber = this._sessionService.getPendingOrderNumber();
    if (pendingOrderNumber) {
      this._router.navigateByUrl('/checkout/' + pendingOrderNumber);
      return;
    }

    if (this.cart) {
      this._router.navigateByUrl('/cart/' + this.cart.uid);
    }
  }



  get navigationLinks() {
    const appType = this._authStateService.getAppType();
    const links = [
      {
        name: 'Website',
        external: true,
        route: 'https://about.hypenation.co.zw',
      },
      {
        name: 'Ts & Cs',
        route: '/terms-conditions',
      },
    ];

    if (appType === 'ticketing') {
      links.unshift({
        name: 'Tickets',
        route: '/tickets',
      });
      links.push({
        name: 'Host an Event',
        external: true,
        route: environment.organiserPortalUrl + '#/auth/signup',
      });
      links.push({
        name: 'Organiser',
        external: true,
        route: environment.organiserPortalUrl + '#/auth/login',
      });
      // Link to Client Portal
      links.push({
        name: 'My Account',
        external: true,
        route: this.getClientPortalUrl(),
      });
    } else if (appType === 'client-portal') {
       links.unshift({
        name: 'Dashboard',
        route: '/',
      });
      links.unshift({
        name: 'Buy Tickets',
        external: true,
        route: this.getTicketingUrl(),
      });
      links.push({
        name: 'My Orders',
        route: '/orders',
      });
      links.push({
        name: 'My Tickets',
        route: '/tickets',
      });
      links.push({
        name: 'Wishlist',
        route: '/wishlist',
      });
    }

    return links;
  }

  private getClientPortalUrl(): string {
    if (window.location.hostname === 'localhost') {
      return 'http://localhost:4202';
    }
    return environment.clientPortalUrl;
  }

  private getTicketingUrl(): string {
    if (window.location.hostname === 'localhost') {
      return 'http://localhost:4200';
    }
    return environment.ticketingUrl;
  }


  ngOnInit(): void {
    this.loadCart();
    this.checkAuthState();
    // Close user menu when clicking outside
    document.addEventListener('click', this.documentClickHandler);

    // Subscribe to auth state changes
    this.authStateSubscription = this._authStateService.onAuthStateChange().subscribe(() => {
      this.checkAuthState();
    });

    // Subscribe to cart updates
    this.cartUpdateSubscription = this._cartStateService.cartUpdate$.subscribe(() => {
      this.syncCartFromState();
    });
  }

  ngOnDestroy(): void {
    document.removeEventListener('click', this.documentClickHandler);
    if (this.authStateSubscription) {
      this.authStateSubscription.unsubscribe();
    }
    if (this.cartUpdateSubscription) {
      this.cartUpdateSubscription.unsubscribe();
    }
  }

  checkAuthState(): void {
    const token = this._authStateService.getToken();
    if (token) {
      this.userEmail = getEmailFromToken(token);
      if (this.userEmail) {
        // Generate initials from email
        const parts = this.userEmail.split('@')[0].split('.');
        if (parts.length >= 2) {
          this.userInitials = (parts[0][0] + parts[1][0]).toUpperCase();
        } else {
          this.userInitials = this.userEmail.substring(0, 2).toUpperCase();
        }
      }
    } else {
      // Clear user info if not authenticated
      this.userEmail = null;
      this.userInitials = '';
    }
  }

  isAuthenticated(): boolean {
    return this._authStateService.isAuthenticated();
  }

  logout(): void {
    const appType = this._authStateService.getAppType();
    this._authStateService.removeTokens();
    this.userEmail = null;
    this.userInitials = '';
    this.isUserMenuOpen = false;
    // Redirect based on app type
    const redirectRoute = appType === 'client-portal' ? '/login' : '/tickets';
    this._router.navigate([redirectRoute]);
  }

  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const userMenuElement = target.closest('.user-menu-container');
    if (!userMenuElement && this.isUserMenuOpen) {
      this.closeUserMenu();
    }
  }

  private loadCart(): void {
    this._cartStateService.resolveCart().subscribe({
      next: (cart) => {
        this.cart = cart;
        this.numberOfItems = cart?.numberOfItems ?? 0;
      },
      error: (err) => {
        console.error('Error resolving cart:', err);
      },
    });
  }

  private syncCartFromState(): void {
    const cart = this._cartStateService.cart();
    this.cart = cart;
    this.numberOfItems = cart?.numberOfItems ?? 0;
  }

  public navigate(route : { route: string; name: string, external?:boolean }):void{
    if(route.external){
      window.open(route.route, '_blank');
    } else {
      this._router.navigateByUrl(route.route);
    }
  }
}
